import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import CardSwiper from "@/components/CardSwiper";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getSiteSettings } from "@/lib/admin";
import { supabase } from "@/lib/supabase";
import { oxpayAPI } from "@/lib/oxpay";
import {
  AlertCircle,
  RefreshCw,
  Clock,
  Loader2,
  XCircle,
  UserPlus,
  MapPin,
  Shield,
  Wallet,
  Settings,
  Edit,
} from "lucide-react";
import { toast } from "react-toastify";
import UserInfoDialog from "@/components/UserInfoDialog";

import bannerImg1 from "@/assets/images/banner3.png";

export default function Home() {
  const navigate = useNavigate();
  const { user: authUser, isAdmin } = useAuth();

  // 유지보수 모드 관련 상태
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [canCheck, setCanCheck] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [isChecking, setIsChecking] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // 사용자 계정 및 카드 데이터
  const [accountData, setAccountData] = useState(null);
  const [cardsData, setCardsData] = useState([]);

  // Supabase 인증에서 가져온 사용자 데이터
  const user = {
    name:
      accountData?.first_name && accountData?.last_name
        ? `${accountData.first_name} ${accountData.last_name}`
        : authUser?.user_metadata?.firstName &&
          authUser?.user_metadata?.lastName
        ? `${authUser.user_metadata.firstName} ${authUser.user_metadata.lastName}`
        : authUser?.email?.split("@")[0] || "User",
    email: authUser?.email || "",
    kycStatus:
      (accountData?.kyc_status === "approved" &&
        accountData?.poa_status === "approved") ||
      false,
    mobileStatus: authUser?.user_metadata?.mobileStatus || false,
    walletStatus: accountData?.wallet_id ? true : false,
    // oxpay_user_id가 있지만 status가 null인 경우도 처리
    oxpayStatus: accountData?.oxpay_user_id
      ? accountData?.oxpay_status || "active" // oxpay_user_id가 있으면 최소한 active로 간주
      : "not_created", // oxpay_user_id가 없으면 not_created
    oxpayUserId: accountData?.oxpay_user_id || null,
  };

  useEffect(() => {
    // isAdmin 값이 설정된 후에 maintenance mode 체크
    if (authUser !== undefined) {
      checkMaintenanceMode();
    }
    fetchAccountData();
    fetchCardsData();
  }, [authUser, isAdmin]);

  useEffect(() => {
    if (authUser?.email) {
      fetchAccountData();
      fetchCardsData();
    }
  }, [authUser?.email]);

  const fetchAccountData = async () => {
    if (!authUser?.email) return;

    try {
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .eq("email", authUser.email)
        .maybeSingle();

      if (data && !error) {
        setAccountData(data);
      }

      // get kyc status
      // const { data: kycData, error: kycError } = await oxpayAPI.getKYCStatus(
      //   data.oxpay_user_id
      // );
      // if (kycData && !kycError) {
      //   console.log("KYC status:", kycData);
      // }
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  };

  const fetchCardsData = async () => {
    if (!authUser?.email) return;

    try {
      const { data, error } = await supabase
        .from("cards")
        .select("*")
        .eq("email", authUser.email)
        .order("created_at", { ascending: false });

      if (data && !error) {
        setCardsData(data);
      }
    } catch (error) {
      console.error("Error fetching cards data:", error);
    }
  };

  useEffect(() => {
    let timer;
    if (maintenanceMode && !isAdmin && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setCanCheck(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [maintenanceMode, isAdmin, timeRemaining]);

  const checkMaintenanceMode = async () => {
    // 관리자는 maintenance mode 체크 skip
    if (isAdmin) {
      setMaintenanceMode(false);
      setInitialLoading(false);
      return;
    }

    if (!initialLoading) {
      setIsChecking(true);
    }
    try {
      const result = await getSiteSettings("system");
      if (result.success && result.data) {
        const isMaintenanceMode =
          result.data.maintenance_mode === "true" ||
          result.data.maintenance_mode === true;
        setMaintenanceMode(isMaintenanceMode);

        // 여전히 유지보수 중이면 타이머 리셋
        if (isMaintenanceMode) {
          setTimeRemaining(60);
          setCanCheck(false);
        }
      } else {
        // maintenance_mode 값이 없으면 false로 설정
        setMaintenanceMode(false);
      }
    } catch (error) {
      console.error("Error checking maintenance mode:", error);
      // 에러가 발생하면 maintenance mode를 false로 설정
      setMaintenanceMode(false);
    } finally {
      setInitialLoading(false);
      setIsChecking(false);
    }
  };

  const handleCheckStatus = () => {
    checkMaintenanceMode();
  };

  const handleDeleteAccount = async () => {
    if (
      !confirm(
        "Are you sure you want to delete your user account? This action cannot be undone."
      )
    ) {
      return;
    }

    if (!accountData?.oxpay_user_id) {
      toast.error("No user account found to delete.");
      return;
    }

    try {
      // 먼저 OxPay API를 호출하여 계정 삭제
      console.log("Deleting OxPay user:", accountData.oxpay_user_id);

      let deleteSuccess = false;

      try {
        const response = await oxpayAPI.deleteUser(accountData.oxpay_user_id);
        console.log("OxPay delete response:", response);

        // 성공 응답 확인
        if (response?.headers?.status_code === "success") {
          console.log("user account deleted successfully");
          deleteSuccess = true;
        } else {
          console.error("OxPay delete failed:", response);

          // localhost에서는 테스트를 위해 계속 진행 가능
          if (window.location.hostname === "localhost") {
            if (
              confirm(
                "OxPay API deletion failed. Continue with local database cleanup?"
              )
            ) {
              deleteSuccess = true;
            }
          }
        }
      } catch (apiError) {
        console.error("OxPay API delete error:", apiError);

        // localhost에서는 테스트를 위해 계속 진행 가능
        if (window.location.hostname === "localhost") {
          if (
            confirm(
              "OxPay API error occurred. Continue with local database cleanup?"
            )
          ) {
            deleteSuccess = true;
          }
        } else {
          toast.error("Failed to delete user account. Please try again.");
        }
      }

      // OxPay API 성공 또는 사용자 확인 후에만 DB 업데이트
      if (deleteSuccess) {
        const { error } = await supabase
          .from("accounts")
          .update({
            // oxpay_user_id: null, // 삭제하면 안됨 - 재활성화를 위해 보존
            oxpay_status: "suspended",
            updated_at: new Date().toISOString(),
          })
          .eq("email", authUser?.email);

        if (error) {
          console.error("Error updating database:", error);
          toast.error("Failed to update local database. Please try again.");
        } else {
          toast.success(
            "Account suspended successfully. You can reactivate it anytime."
          );
          // 데이터 새로고침
          fetchAccountData();
          fetchCardsData();
        }
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account. Please try again.");
    }
  };

  // 초기 유지보수 상태 확인 중 로딩 스피너 표시
  if (initialLoading) {
    return (
      <div>
        <Header showBack={false} />
        <div className="p-4">
          <div className="w-full max-w-md mx-auto flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  // 관리자가 아닌 사용자에게 유지보수 모드 메시지 표시
  if (maintenanceMode && !isAdmin) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header showBack={false} />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <Alert className="border-yellow-600 bg-yellow-50 dark:bg-yellow-950/20">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <AlertTitle className="text-yellow-800 dark:text-yellow-200">
                Site Under Maintenance
              </AlertTitle>
              <AlertDescription className="text-yellow-700 dark:text-yellow-300">
                <p className="mb-4">
                  We are currently performing system maintenance. Please try
                  again later.
                </p>
                {!canCheck && (
                  <div className="flex items-center gap-2 mb-4 text-sm">
                    <Clock className="h-4 w-4" />
                    <span>
                      Please wait {timeRemaining} seconds before checking again
                    </span>
                  </div>
                )}
                <Button
                  onClick={handleCheckStatus}
                  variant="outline"
                  className="w-full mt-4"
                  disabled={!canCheck || isChecking}
                >
                  {isChecking ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Check Status
                    </>
                  )}
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <Header showBack={false} />

      <div className="p-4 mb-8">
        <div className="w-full max-w-md mx-auto flex flex-col gap-2.5 md:gap-5">
          {/* Banner Image */}
          <div className="p-2">
            <img
              src={bannerImg1}
              alt="Banner"
              className="w-full h-full object-cover rounded-lg"
            />
          </div>

          {/* User Info and Status Cards */}
          <div className="flex flex-col gap-3">
            {/* Welcome Card */}
            <div className="border border-foreground/20 rounded-[10px] bg-card/50 overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between">
                <span className="font-bold text-base text-foreground">
                  Hello, {user.name}
                </span>
                <UserInfoDialog oxpayUserId={accountData?.oxpay_user_id} />
              </div>
              <div className="px-4 pb-3 flex items-center gap-2">
                <span className="text-xs text-foreground/70">Email:</span>
                <span className="text-xs text-foreground">{user.email}</span>
              </div>
            </div>

            {/* Registration Progress Card */}
            <div className="border border-foreground/20 rounded-[10px] bg-card/50 overflow-hidden">
              <div className="bg-foreground/10 px-4 py-2 border-b border-foreground/20">
                <h3 className="text-sm font-semibold text-foreground">
                  Registration Progress
                </h3>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-foreground flex items-center gap-1">
                    <UserPlus className="w-3 h-3" />
                    User Registration
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-bold ${
                        user.oxpayStatus === "active"
                          ? "text-green-500"
                          : user.oxpayStatus === "suspended"
                          ? "text-yellow-500"
                          : "text-[#f09191]"
                      }`}
                    >
                      {user.oxpayStatus === "active"
                        ? "Registered"
                        : user.oxpayStatus === "suspended"
                        ? "Suspended"
                        : "Not Registered"}
                    </span>
                    {/* Edit profile icon for registered users (only before KYC approval) */}
                    {user.oxpayStatus === "active" && !user.kycStatus && (
                      <button
                        onClick={() => navigate("/edit-user")}
                        className="text-foreground/60 hover:text-foreground transition-colors cursor-pointer"
                        title="Edit profile"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {user.oxpayStatus !== "not_created" &&
                      window.location.hostname === "localhost" && (
                        <button
                          onClick={handleDeleteAccount}
                          className="text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                          title="Delete user account"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      )}
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Input Address
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-bold ${
                        accountData?.oxpay_address_completed === true
                          ? "text-green-500"
                          : accountData?.oxpay_address_completed === false
                          ? "text-yellow-500"
                          : "text-[#919191]"
                      }`}
                    >
                      {accountData?.oxpay_address_completed === true
                        ? "Completed"
                        : accountData?.oxpay_address_completed === false
                        ? "Pending"
                        : "Not Completed"}
                    </span>
                    {/* Show gear icon if address is completed but KYC is not approved */}
                    {accountData?.oxpay_address_completed === true && !user.kycStatus && (
                      <button
                        onClick={() => navigate("/input-address")}
                        className="text-foreground/60 hover:text-foreground transition-colors cursor-pointer"
                        title="Edit address"
                      >
                        <Settings className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-foreground flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    KYC Verification
                  </span>
                  <span
                    className={`text-sm font-bold ${
                      user.kycStatus ? "text-green-500" : "text-[#f09191]"
                    }`}
                  >
                    {user.kycStatus ? "Approved" : "Not Approved"}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-foreground flex items-center gap-1">
                    <Wallet className="w-3 h-3" />
                    Wallet Creation
                  </span>
                  <span
                    className={`text-sm font-bold ${
                      user.walletStatus ? "text-green-500" : "text-[#f09191]"
                    }`}
                  >
                    {user.walletStatus ? "Created" : "Not Created"}
                  </span>
                </div>
              </div>
            </div>

            {/* OxPay Registration */}
            {(user.oxpayStatus === "not_created" ||
              user.oxpayStatus === "suspended") && (
              <div className="border border-foreground/50 rounded-[10px] p-5 flex flex-col items-center gap-5 relative">
                <h3 className="text-base font-semibold text-foreground">
                  {user.oxpayStatus === "suspended"
                    ? "Reactivate Account"
                    : "Register User"}
                </h3>
                <p className="text-xs text-foreground/80 text-center leading-relaxed">
                  {user.oxpayStatus === "suspended"
                    ? "Your account is suspended. Please reactivate to continue."
                    : "Register user to create and manage your card."}
                </p>

                {/* 도움말 아이콘 */}
                <div className="absolute top-1 right-1 w-4 h-4 rounded-full border border-[#808080] flex items-center justify-center">
                  <span className="text-[10px] text-[#747474]">?</span>
                </div>

                <Button
                  onClick={() => navigate("/register-user")}
                  className="h-6 px-2 text-[10px] font-semibold"
                >
                  {user.oxpayStatus === "suspended"
                    ? "Reactivate Now"
                    : "Register Now"}
                </Button>
              </div>
            )}

            {/* Address Input Card - After User Registration */}
            {user.oxpayStatus === "active" &&
              !accountData?.oxpay_address_completed && (
                <div className="border border-foreground/50 rounded-[10px] p-5 flex flex-col items-center gap-5 relative">
                  <h3 className="text-base font-semibold text-foreground">
                    Input Your Address
                  </h3>
                  <p className="text-xs text-foreground/80 text-center leading-relaxed">
                    Please enter your residential address information.
                  </p>

                  {/* 도움말 아이콘 */}
                  <div className="absolute top-1 right-1 w-4 h-4 rounded-full border border-[#808080] flex items-center justify-center">
                    <span className="text-[10px] text-[#747474]">?</span>
                  </div>

                  <Button
                    onClick={() => navigate("/input-address")}
                    className="h-6 px-2 text-[10px] font-semibold"
                  >
                    Input Address
                  </Button>
                </div>
              )}

            {/* KYC Verification Card - After Address Input is Completed */}
            {user.oxpayStatus === "active" &&
              accountData?.oxpay_address_completed &&
              !user.kycStatus && (
                <div className="border border-foreground/50 rounded-[10px] p-5 flex flex-col items-center gap-5 relative">
                  <h3 className="text-base font-semibold text-foreground">
                    Get KYC Verification
                  </h3>
                  <p className="text-xs text-foreground/80 text-center leading-relaxed">
                    Complete identity verification to activate your card.
                  </p>

                  {/* Question Mark Icon */}
                  <div className="absolute top-1 right-1 w-4 h-4 rounded-full border border-[#808080] flex items-center justify-center">
                    <span className="text-[10px] text-[#747474]">?</span>
                  </div>

                  <Button
                    onClick={() => navigate("/kyc-onfido")}
                    className="h-6 px-2 text-[10px] font-semibold"
                  >
                    Start KYC
                  </Button>
                </div>
              )}

            {/* Create Wallet Card - After KYC is Completed */}
            {user.oxpayStatus === "active" &&
              user.kycStatus &&
              !user.walletStatus && (
                <div className="border border-foreground/50 rounded-[10px] p-5 flex flex-col items-center gap-5 relative">
                  <h3 className="text-base font-semibold text-foreground">
                    Create Wallet
                  </h3>
                  <p className="text-xs text-foreground/80 text-center leading-relaxed">
                    Set up your digital wallet to manage funds and cards.
                  </p>

                  {/* Question Mark Icon */}
                  <div className="absolute top-1 right-1 w-4 h-4 rounded-full border border-[#808080] flex items-center justify-center">
                    <span className="text-[10px] text-[#747474]">?</span>
                  </div>

                  <Button
                    onClick={() => navigate("/create-wallet")}
                    className="h-6 px-2 text-[10px] font-semibold"
                  >
                    Create Wallet
                  </Button>
                </div>
              )}

            {/* Congratulations Card - After All Steps Completed */}
            {user.oxpayStatus === "active" &&
              user.kycStatus &&
              user.walletStatus && (
                <div className="border-2 border-green-500 dark:border-green-600 bg-green-50/50 dark:bg-green-950/20 rounded-[10px] p-5 flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <h3 className="text-base font-bold text-foreground">
                      Congratulations! 🎉
                    </h3>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm text-foreground">
                      All procedures for card registration have been completed
                      successfully.
                    </p>

                    <div className="space-y-2">
                      <p className="text-sm text-foreground/80">
                        • Once your card is delivered, you can register it on
                        the card purchase site.
                      </p>
                      <p className="text-sm text-foreground/80">
                        • After registration, top up your card to start using
                        it.
                      </p>
                      <p className="text-sm text-foreground/80">
                        • You can check your card transaction history on this
                        site.
                      </p>
                    </div>

                    {/* Important Notice - Emphasized */}
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-500 dark:border-yellow-600 rounded-lg p-3 mt-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                            Important Security Notice
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-300">
                            If your card is lost or stolen, you can immediately
                            lock it or report it as stolen through the [Card]
                            menu. This will protect your funds from unauthorized
                            use.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Mobile Verification Card */}
            {/* {!user.mobileStatus && (
              <div className="border border-foreground/50 rounded-[10px] p-5 flex flex-col items-center gap-5 relative">
                <h3 className="text-base font-semibold text-foreground">
                  Mobile Verification
                </h3>
                <p className="text-xs text-foreground/80 text-center leading-relaxed">
                  Verify your mobile number to enable all features.
                </p>

                {/* 도움말 아이콘 */}
            {/*    <div className="absolute top-1 right-1 w-4 h-4 rounded-full border border-[#808080] flex items-center justify-center">
                  <span className="text-[10px] text-[#747474]">?</span>
                </div>

                <Button
                  onClick={() => console.log("Navigate to Mobile verification")}
                  className="h-6 px-2 text-[10px] font-semibold"
                >
                  Verify Now
                </Button>
              </div>
            )} */}

            {/* Card Swiper Component */}
            {/* <Card className="bg-card/80 p-4">
              <CardSwiper />
            </Card> */}

            {/* Card Swiper Component */}
            {/* <CardSwiper userEmail={user.email} cardsData={cardsData} /> */}
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar */}
      <BottomBar />
    </div>
  );
}
